/* eslint-disable react-refresh/only-export-components */
import {
  THEME_OWNER_STORAGE_KEY,
  THEME_STORAGE_KEY,
} from "@frontend-shared/constants";
import { EThemePreference } from "@frontend-shared/types";
import {
  getHasAnyProjects,
  getMsUntilNextThemeBoundary,
  hasOwnOrgBranding,
  parseThemePreference,
  resolveThemeScheme,
  type TResolvedThemeScheme,
} from "@frontend-shared/utils";
import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { useUserPreferences } from "@/api/preferences/useUserPreferences";
import { clearRuntimeBrandingCss } from "@/api/theme/runtime-branding-css";
import type { IResolvedBranding, TThemeMode } from "@/api/theme/types";
import { useApplyOrgBranding } from "@/api/theme/useApplyOrgBranding";
import { useApplyPublicBranding } from "@/api/theme/useApplyPublicBranding";
import { AuthContext } from "@/contexts/AuthContext";
import { getOrgFaviconUrl, setCustomFaviconHref } from "@/lib/favicon";

// NOTE: NSAI-5650 — aliased to the shared enum rather than re-declared as a
// string union. The union had drifted into a second source of truth that this
// file then had to assert its way back out of, and `auto` would have had to be
// added to both.
export type TTheme = EThemePreference;

const SYSTEM_COLOR_SCHEME_DARK_QUERY = "(prefers-color-scheme: dark)";

// NOTE: How long transitions stay blanked after a scheme flip — long enough for
// the new palette to paint, short enough not to swallow a real interaction.
const TRANSITION_SUPPRESSION_MS = 100;

// NOTE: NSAI-5822 — how long the first paint waits for the account's saved
// theme before giving up and painting the OS-derived guess. Long enough to
// cover a normal preferences round-trip, short enough that a stalled one costs
// a beat rather than the session.
const THEME_SETTLE_DEADLINE_MS = 1500;

export interface ILogoCacheBusters {
  light: number | null;
  dark: number | null;
}

interface IThemeProviderState {
  theme: TTheme;
  setTheme: (theme: TTheme) => void;
  // NOTE: NSAI-5822 — the scheme actually painted on the document. `theme` is
  // the saved preference, and under `system` or `auto` the provider re-resolves
  // it on an OS colour-scheme change or at a schedule boundary without the
  // preference changing, so anything that needs to know which way the screen
  // currently points (the header toggle) reads this rather than re-deriving it.
  resolvedScheme: TResolvedThemeScheme;
  isOrgBrandingEnabled: boolean;
  hasCustomBranding: boolean;
  // NOTE: NSAI-6427 — `hasCustomBranding` answers a narrower question than its
  // name suggests: it is true only for a CUSTOM STYLESHEET, because that is all
  // the branding lookup used to report. `hasOwnBranding` is the whole answer —
  // stylesheet, uploaded logo or product name — and it is the one anything
  // deciding whether to render NodeShift's own artwork has to ask. The two stay
  // separate because `theme.css` scopes the NodeShift sidebar tint on the
  // stylesheet question specifically (via `data-custom-branding`), and widening
  // that would restyle the sidebar of every organization that uploaded only a
  // logo.
  hasOwnBranding: boolean;
  // NOTE: NSAI-5315 — hasCustomBranding starts false, which is
  // indistinguishable from "this organization is on the default appearance"
  // until the branding lookup answers. Consumers that render default-appearance
  // artwork must wait for this flag, otherwise a branded visitor gets a flash of
  // NodeShift artwork (and a layout shift) before the response lands.
  isBrandingResolved: boolean;
  // NOTE: NSAI-6427 follow-up — an uploaded logo of the organization's own,
  // which `hasOwnBranding` deliberately blurs together with a stylesheet and a
  // product name. Anything that RENDERS the logo needs them apart: `/theme/logo`
  // answers 200 with the running flavor's wordmark when the organization
  // uploaded none, so "branded" is not enough to justify drawing it.
  hasOwnLogo: boolean;
  // NOTE: NSAI-6321 — `isBrandingResolved` narrowed to the organization on
  // screen. Anything that renders one organization's IDENTITY — its product
  // name, its logo — waits on this one rather than on `isBrandingResolved`,
  // which latches across a project switch; see the note where it is derived.
  isBrandingResolvedForProject: boolean;
  // NOTE: NSAI-5822 — false only while a signed-in visitor's appearance is
  // still unknown on THIS browser: the saved theme, while nothing this account
  // owns is cached locally and the preferences request has not answered, or
  // the organization's branding, until /theme/me has answered. The app holds
  // its first paint on this: without it the boot guesses the scheme from the
  // OS — or repaints whichever account last used the browser — and keeps the
  // default sidebar tint until the branding lands, so an account set to dark on
  // a light machine got a full white screen for the length of a request and
  // then a hard flip. Always true for a signed-out visitor, for a returning
  // one (its own preference is cached, and branding is settled once per
  // session), and once the bounded waits below pass.
  isThemeSettled: boolean;
  // NOTE: Appearance saves/resets apply the branding CSS locally instead of
  // re-running /theme/me, so they push the newly saved CSS through here to keep
  // hasCustomBranding accurate without a reload or project switch.
  syncCustomBranding: (cssContent: string) => void;
  // NOTE: NSAI-6427 — pushes a just-saved (or just-reset) logo into the running
  // shell, the way `syncCustomFavicon` does for the tab icon. Without it an
  // organization whose only branding is the logo it just uploaded keeps the
  // platform mark in the empty chat until a reload, because `hasOwnBranding`
  // would still be reading the answer /theme/me gave before the upload.
  syncOwnLogo: (hasLogo: boolean) => void;
  // NOTE: NSAI-6321 — the organization's own product name, or null when it has
  // not set one. Read through `useAppName`, which is what applies the flavor
  // default; nothing should consume this directly.
  appName: string | null;
  // NOTE: NSAI-6321 — pushes a just-saved (or just-cleared) platform name into
  // the running shell, the same way `syncCustomBranding` does for the CSS, so
  // the tab title follows a save without a reload or a project switch.
  syncAppName: (appName: string | null) => void;
  // NOTE: NSAI-6321 — the custom favicon's URL, or null when the organization
  // is on the flavor default. The tab icon itself is written by `lib/favicon`;
  // this is here so the unread badge knows which image to paint over.
  customFaviconUrl: string | null;
  // NOTE: Busts the favicon URL after an upload. Branding reads only update
  // the favicon flag so normal page loads can reuse the server's cached image.
  // Separate from the logo busters so saving an icon does not reload logos.
  bumpFaviconCacheBuster: () => void;
  syncCustomFavicon: (hasFavicon: boolean) => void;
  logoCacheBusters: ILogoCacheBusters;
  // NOTE: Refreshes the cached org logo(s): pass the mode that changed to
  // re-fetch only that logo, or omit it to refresh both (logo reset,
  // org-branding resolution).
  bumpLogoCacheBuster: (mode?: TThemeMode) => void;
}

const ThemeProviderContext = createContext<IThemeProviderState | null>(null);

interface IThemeProviderProps extends PropsWithChildren {
  isOrgBrandingEnabled?: boolean;
}

function disableTransition() {
  const style = document.createElement("style");
  style.dataset.styles = "inline";
  style.innerHTML = "*, *::before, *::after {transition: none !important;}";
  style.dataset.disableTransition = "true";

  document.head.appendChild(style);
  const clear = () => {
    const styles = document.querySelectorAll("[data-disable-transition]");
    for (const style of styles) {
      style.remove();
    }
  };
  return clear;
}

export function ThemeProvider({
  children,
  isOrgBrandingEnabled = true,
}: Readonly<IThemeProviderProps>) {
  const clearStylesRef = useRef<() => void>(() => {});
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const { i18n } = useTranslation();
  const [logoCacheBusters, setLogoCacheBusters] = useState<ILogoCacheBusters>({
    light: null,
    dark: null,
  });
  const bumpLogoCacheBuster = useCallback((mode?: TThemeMode): void => {
    const stamp = Date.now();
    setLogoCacheBusters((previous) => ({
      light: mode === "dark" ? previous.light : stamp,
      dark: mode === "light" ? previous.dark : stamp,
    }));
  }, []);
  const [faviconCacheBuster, setFaviconCacheBuster] = useState<number | null>(
    null,
  );
  const [hasCustomFavicon, setHasCustomFavicon] = useState(false);
  // NOTE: NSAI-8007 — the favicon's digest from the branding answer. Unlike the
  // cache buster it survives a reload, so the URL a fresh page asks for is the
  // current image's, not the one the browser cached before an upload.
  const [faviconVersion, setFaviconVersion] = useState<string | null>(null);
  const [appName, setAppName] = useState<string | null>(null);
  const [hasCustomBranding, setHasCustomBranding] = useState(false);
  const [hasOwnLogo, setHasOwnLogo] = useState(false);
  const [isBrandingSettled, setIsBrandingSettled] = useState(false);
  const [theme, setTheme] = useState<TTheme>(() =>
    parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY)),
  );
  // NOTE: Read once, at boot: whether this browser already knew the account's
  // theme. Re-reading later would go true the moment the response is cached and
  // defeat the point. A lazy useState rather than a ref because this is read
  // during render, which is exactly what refs are not for.
  const [hasCachedTheme] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) !== null,
  );
  // NOTE: Whose theme the cache holds. The theme key outlives a sign-out (the
  // sign-in screen keeps the last look), so a cached value alone proves nothing
  // about the account signing in next: on a shared browser it is the previous
  // account's, and painting it would be the very flash this gate exists to
  // prevent. Stamped once an account's preferences have been applied; absent on
  // browsers that cached a theme before the stamp existed, which reads as
  // "unknown" and waits once.
  const [cachedThemeOwner, setCachedThemeOwner] = useState<string | null>(() =>
    localStorage.getItem(THEME_OWNER_STORAGE_KEY),
  );
  // NOTE: The wait is bounded. A preferences request that fails resolves this
  // through `isFetched`, but one that merely hangs would otherwise hold the
  // whole app on the loader — a flash is bad, an app that never paints is worse.
  // Keyed by account rather than a plain flag: a deadline that passed for the
  // account that signed out must not excuse the one signing in after it.
  const [settleDeadlinePassedFor, setSettleDeadlinePassedFor] = useState<
    string | null
  >(null);
  // NOTE: The same bound for the organization's branding, keyed by project for
  // the same reason: a deadline that passed for the project last shown must
  // not excuse the one the account switches to next.
  const [brandingDeadlinePassedFor, setBrandingDeadlinePassedFor] = useState<
    string | null
  >(null);
  const [resolvedScheme, setResolvedScheme] = useState<TResolvedThemeScheme>(
    () =>
      resolveThemeScheme({
        preference: theme,
        isDeviceDark: globalThis.matchMedia(SYSTEM_COLOR_SCHEME_DARK_QUERY)
          .matches,
      }),
  );

  // NOTE: NSAI-5650 — all three ways the rendered scheme can flip (the user
  // picking a theme, the OS scheme changing under `system`, the clock crossing a
  // boundary under `auto`) have to blank transitions for a frame, so the ref
  // dance that does it lives here once rather than once per call site.
  const suppressTransition = useCallback((): void => {
    clearStylesRef.current = disableTransition();
    globalThis.clearTimeout(timeoutRef.current);
    timeoutRef.current = globalThis.setTimeout(() => {
      clearStylesRef.current();
    }, TRANSITION_SUPPRESSION_MS);
  }, []);

  useEffect(() => {
    const root = globalThis.document.documentElement;
    const mediaQuery = globalThis.matchMedia(SYSTEM_COLOR_SCHEME_DARK_QUERY);

    const applyResolvedScheme = (): void => {
      const scheme = resolveThemeScheme({
        preference: theme,
        isDeviceDark: mediaQuery.matches,
      });

      root.classList.remove("light", "dark");
      root.classList.add(scheme);
      setResolvedScheme(scheme);
    };

    applyResolvedScheme();

    if (theme === EThemePreference.SYSTEM) {
      const onSystemThemeChange = (): void => {
        suppressTransition();
        applyResolvedScheme();
      };

      mediaQuery.addEventListener("change", onSystemThemeChange);

      return (): void => {
        mediaQuery.removeEventListener("change", onSystemThemeChange);
      };
    }

    if (theme !== EThemePreference.AUTO) {
      return undefined;
    }

    let boundaryTimeoutId: ReturnType<typeof setTimeout>;

    // NOTE: NSAI-5650 — sleeps until the exact instant the schedule flips and
    // then re-arms from the clock again, rather than ticking on a fixed
    // interval: one timer per half-day instead of a poll, and each wake-up
    // recomputes its own deadline so drift cannot accumulate across days.
    const scheduleBoundary = (): void => {
      boundaryTimeoutId = globalThis.setTimeout(() => {
        suppressTransition();
        applyResolvedScheme();
        scheduleBoundary();
      }, getMsUntilNextThemeBoundary());
    };

    // NOTE: NSAI-5650 — a background tab has its timers throttled and a
    // suspended machine does not run them at all, so a tab left open across
    // 19:00 would come back still painted for the afternoon. Re-resolving on the
    // way back to visible is what makes the schedule hold across a lunch break
    // or an overnight sleep, not just while the tab is in front.
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== "visible") {
        return;
      }

      // NOTE: suppressed like every other flip. This path is the one that
      // repaints a whole screenful at once — the tab comes back after the
      // boundary already passed — so without it the correction plays out as
      // every transitioning property animating at the moment the user looks
      // back at the tab.
      suppressTransition();
      applyResolvedScheme();
      globalThis.clearTimeout(boundaryTimeoutId);
      scheduleBoundary();
    };

    scheduleBoundary();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return (): void => {
      globalThis.clearTimeout(boundaryTimeoutId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [suppressTransition, theme]);

  const handleSetTheme = useCallback(
    (theme: TTheme): void => {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      setTheme(theme);
      suppressTransition();
    },
    [suppressTransition],
  );

  useEffect(
    () => () => {
      clearStylesRef.current?.();
      globalThis.clearTimeout(timeoutRef.current);
    },
    [],
  );

  // NOTE: For admin routes, AuthContext may not be available
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const userUuid = user?.uuid ?? null;
  const currentProjectUuid = authContext?.currentProjectUuid ?? null;
  const projects = authContext?.projects ?? null;
  const isAuthResolving = authContext?.isLoading ?? false;

  const bumpFaviconCacheBuster = useCallback((): void => {
    setFaviconCacheBuster(Date.now());
  }, []);

  const syncCustomBranding = useCallback((cssContent: string): void => {
    setHasCustomBranding(cssContent.trim().length > 0);
    setIsBrandingSettled(true);
  }, []);

  // NOTE: NSAI-6427 — the logo half of the same answer. Kept out of
  // `syncCustomBranding` because that one is also called from the Appearance
  // screen with a just-saved stylesheet in hand and nothing to say about the
  // logo; folding the two together would have a CSS save clear the logo flag.
  const syncOwnLogo = useCallback((nextHasLogo: boolean): void => {
    setHasOwnLogo(nextHasLogo);
  }, []);

  // NOTE: NSAI-6321 — a blank name is stored as "no name", so the Appearance
  // screen and the branding lookup agree on one representation and the tab
  // title never ends up empty.
  const syncAppName = useCallback((nextAppName: string | null): void => {
    setAppName(nextAppName?.trim() || null);
  }, []);

  // NOTE: /theme/me resolves on every boot; changing the URL here would bypass
  // the favicon endpoint's five-minute HTTP cache on every page load.
  const syncCustomFavicon = useCallback((nextHasFavicon: boolean): void => {
    setHasCustomFavicon(nextHasFavicon);
  }, []);

  const handleBrandingResolved = useCallback(
    ({
      cssContent,
      appName: resolvedAppName,
      hasFavicon,
      faviconVersion: resolvedFaviconVersion,
      hasLogo,
    }: IResolvedBranding) => {
      // NOTE: The resolved org may have different logos for both modes.
      bumpLogoCacheBuster();
      syncCustomBranding(cssContent);
      syncOwnLogo(hasLogo);
      syncAppName(resolvedAppName);
      syncCustomFavicon(hasFavicon);
      setFaviconVersion(resolvedFaviconVersion);
    },
    [
      bumpLogoCacheBuster,
      syncCustomBranding,
      syncOwnLogo,
      syncAppName,
      syncCustomFavicon,
    ],
  );

  const handleBrandingCleared = useCallback(() => {
    setHasCustomBranding(false);
    setHasOwnLogo(false);
    setIsBrandingSettled(true);
    // NOTE: A branding lookup that failed must leave the flavor default in
    // place — the same "fall back, never blank" rule hasCustomBranding follows.
    setAppName(null);
    setHasCustomFavicon(false);
    setFaviconVersion(null);
  }, []);

  // NOTE: NSAI-5822 — which project the ORGANIZATION lookup has answered for.
  // `isBrandingSettled` cannot stand in for this on the boot gate: the PUBLIC
  // lookup runs while there is no session and sets that same flag, so by the
  // time the account signs in and /theme/me starts, a bare "some branding
  // lookup answered" already read settled — and the boot released with the
  // default NodeShift tint for the whole length of the org request, then
  // flipped. Keyed by project so the answer always belongs to what is on
  // screen.
  const [orgBrandingSettledFor, setOrgBrandingSettledFor] = useState<
    string | null
  >(null);

  const handleOrgBrandingResolved = useCallback(
    (branding: IResolvedBranding): void => {
      handleBrandingResolved(branding);
      setOrgBrandingSettledFor(currentProjectUuid);
    },
    [handleBrandingResolved, currentProjectUuid],
  );

  const handleOrgBrandingCleared = useCallback((): void => {
    handleBrandingCleared();
    setOrgBrandingSettledFor(currentProjectUuid);
  }, [handleBrandingCleared, currentProjectUuid]);

  useEffect(() => {
    if (isOrgBrandingEnabled) {
      return;
    }

    clearRuntimeBrandingCss();
    setHasCustomBranding(false);
    setHasOwnLogo(false);
    // NOTE: NSAI-6321 — the admin shell runs with org branding off, so it keeps
    // the platform's own name and tab icon rather than the last organization's.
    setAppName(null);
    setHasCustomFavicon(false);
    setFaviconVersion(null);
  }, [isOrgBrandingEnabled]);

  const isOrgBrandingRequested =
    isOrgBrandingEnabled && !!user && !!currentProjectUuid;
  const isPublicBrandingRequested = isOrgBrandingEnabled && !user;

  // NOTE: NSAI-5315 — "settled" once a lookup answered; when neither lookup runs
  // (branding disabled, or a session without a resolved project) there is
  // nothing in flight to wait for, so that state counts as resolved too.
  const isBrandingResolved =
    isBrandingSettled ||
    (!isOrgBrandingRequested && !isPublicBrandingRequested);

  // NOTE: NSAI-6321 — the same question as `isBrandingResolved`, asked about
  // the organization currently on screen rather than about any organization.
  //
  // NOTE: NSAI-6427 — renamed from `isAppNameResolved`, because the logo needs
  // exactly the same guarantee for exactly the same reason: it is that
  // organization's identity, and painting it from a latched flag paints the
  // PREVIOUS organization's.
  // `isBrandingSettled` latches once and never unlatches, which is right for
  // the illustration rule but wrong for the product name: on a switch between
  // two branded organizations it reads settled while `/theme/me` is still out
  // for the new one, so `appName` is still the PREVIOUS organization's. Writing
  // the tab title from that stamps one customer's name onto another customer's
  // pages for the length of a request. Keyed by project, the title simply is
  // not rewritten until the answer belongs to what is on screen.
  const isBrandingResolvedForProject =
    isBrandingResolved &&
    (!isOrgBrandingRequested || orgBrandingSettledFor === currentProjectUuid);

  // NOTE: NSAI-6427 — the whole branding answer, as opposed to the stylesheet
  // half `hasCustomBranding` reports. Any ONE of the three is already a customer
  // putting their own product on screen, which is why an organization that
  // uploaded nothing but a logo must stop counting as unbranded.
  const hasOwnBranding = useMemo(
    (): boolean =>
      hasOwnOrgBranding({
        hasCustomCss: hasCustomBranding,
        hasLogo: hasOwnLogo,
        appName,
      }),
    [hasCustomBranding, hasOwnLogo, appName],
  );

  // NOTE: NSAI-5822 — theme.css scopes the NodeShift-only sidebar tint with
  // :not([data-custom-branding]), so an org shipping its own branding CSS keeps
  // the sidebar following the --background-secondary it configured.
  //
  // A layout effect rather than a passive one: the branding answer is what
  // releases the first paint below, and in that same commit the app mounts —
  // a passive effect could run after that frame was painted, which would show
  // the NodeShift tint for exactly the one frame the wait was meant to spare.
  useLayoutEffect(() => {
    const root = globalThis.document.documentElement;
    if (hasCustomBranding) {
      root.dataset.customBranding = "true";
    } else {
      delete root.dataset.customBranding;
    }
  }, [hasCustomBranding]);

  // NOTE: NSAI-6321 — null unless the organization actually has a favicon, so
  // the tab never points at an endpoint that would answer 404.
  //
  // NOTE: NSAI-6475 — scoped by `orgBrandingSettledFor`, the project the
  // branding answer belongs to, rather than `currentProjectUuid`: the URL moves
  // to the new organization only once its own answer (and cookie) is in, never
  // while the previous organization's `hasCustomFavicon` is still standing.
  const customFaviconUrl = useMemo(
    (): string | null =>
      hasCustomFavicon
        ? getOrgFaviconUrl({
            cacheBuster: faviconCacheBuster,
            projectUuid: orgBrandingSettledFor,
            version: faviconVersion,
          })
        : null,
    [
      hasCustomFavicon,
      faviconCacheBuster,
      orgBrandingSettledFor,
      faviconVersion,
    ],
  );

  // NOTE: NSAI-6321 — `lib/favicon` owns the <link rel="icon"> element; this
  // only declares which image the branding half wants. The unread badge writes
  // its own slot there, so the two no longer race to overwrite each other.
  useEffect(() => {
    setCustomFaviconHref(customFaviconUrl);
  }, [customFaviconUrl]);

  useApplyOrgBranding({
    currentProjectUuid,
    enabled: isOrgBrandingRequested,
    onBrandingResolved: handleOrgBrandingResolved,
    onBrandingCleared: handleOrgBrandingCleared,
  });

  // NOTE: NSAI-5315 — mutually exclusive with the call above: /theme/me owns the
  // branding once there is a session, this owns it before there is one. Without
  // it hasCustomBranding stays false on every unauthenticated screen, so the
  // illustration rule ("default appearance shows it, a custom appearance hides
  // it") could only ever fire on the authenticated organization screen.
  useApplyPublicBranding({
    enabled: isPublicBrandingRequested,
    onBrandingResolved: handleBrandingResolved,
    onBrandingCleared: handleBrandingCleared,
  });

  const { isFetched: isPreferencesFetched, isSuccess: isPreferencesApplied } =
    useUserPreferences({
      i18n,
      theme,
      setTheme: handleSetTheme,
      enabled: !!user,
    });

  useEffect(() => {
    if (!userUuid) return undefined;

    const timer = globalThis.setTimeout(
      () => setSettleDeadlinePassedFor(userUuid),
      THEME_SETTLE_DEADLINE_MS,
    );

    return (): void => globalThis.clearTimeout(timer);
  }, [userUuid]);

  useEffect(() => {
    if (!isOrgBrandingRequested || !currentProjectUuid) return undefined;

    const timer = globalThis.setTimeout(
      () => setBrandingDeadlinePassedFor(currentProjectUuid),
      THEME_SETTLE_DEADLINE_MS,
    );

    return (): void => globalThis.clearTimeout(timer);
  }, [isOrgBrandingRequested, currentProjectUuid]);

  // NOTE: The cache is stamped with its owner only once that account's saved
  // theme has actually been applied, so a stamp always vouches for the value
  // next to it. A request that failed leaves the stamp alone: the value in the
  // cache may still be the previous account's.
  useEffect(() => {
    if (!userUuid || !isPreferencesApplied) return;

    localStorage.setItem(THEME_OWNER_STORAGE_KEY, userUuid);
    setCachedThemeOwner(userUuid);
  }, [userUuid, isPreferencesApplied]);

  // NOTE: The organization's branding is the other half of the first paint:
  // theme.css keeps the NodeShift sidebar tint only while the root carries no
  // data-custom-branding, and that attribute is stamped from /theme/me, so a
  // branded organization painted the default tint for the length of that
  // request and then flipped. Settled once the ORGANIZATION lookup answered for
  // the project on screen, either way, or once its bounded wait ran out.
  //
  // NSAI-5822 — "nothing to look up" has to mean *known* to be nothing. The
  // project is chosen in an effect that runs once the projects list lands, so
  // there is one commit where auth has finished loading (`isLoading` already
  // covers the projects request), the account does have projects, and
  // `currentProjectUuid` is still null. Reading that commit as "no project, so
  // no branding to wait for" released the paint a frame before the attribute
  // was stamped — the very flip this gate exists to prevent. The null project
  // also made the deadline comparison below vacuously true, so both halves had
  // to be keyed off a project that actually exists.
  const isBrandingLookupPending =
    isOrgBrandingEnabled &&
    !!user &&
    (currentProjectUuid
      ? orgBrandingSettledFor !== currentProjectUuid
      : getHasAnyProjects(projects));

  // NOTE: While the project is still being chosen there is nothing to key a
  // project deadline to, so that phase is bounded by the account's deadline
  // instead — it is armed as soon as there is a user.
  const hasBrandingWaitExpired = currentProjectUuid
    ? brandingDeadlinePassedFor === currentProjectUuid
    : settleDeadlinePassedFor === userUuid;

  const isOrgBrandingSettledNow =
    !isBrandingLookupPending || hasBrandingWaitExpired;

  // NOTE: Only the FIRST paint of a session waits for branding. Once the app is
  // up, switching project swaps the branding in place — dropping the loader
  // back over a running app would be a worse flicker than the one this gate
  // exists to prevent. Latched per account, and only once auth has finished
  // resolving: before that the gate is trivially "settled" (no user, no
  // projects yet), and latching on that would defeat it for the real boot.
  const [brandingReleasedFor, setBrandingReleasedFor] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (isAuthResolving || !userUuid || !isOrgBrandingSettledNow) return;
    setBrandingReleasedFor(userUuid);
  }, [isAuthResolving, userUuid, isOrgBrandingSettledNow]);

  const isOrgBrandingSettled =
    isOrgBrandingSettledNow || (!!userUuid && brandingReleasedFor === userUuid);

  const isThemeSettled =
    (!userUuid ||
      (hasCachedTheme && cachedThemeOwner === userUuid) ||
      isPreferencesFetched ||
      settleDeadlinePassedFor === userUuid) &&
    isOrgBrandingSettled;

  const value = useMemo(
    () => ({
      theme,
      setTheme: handleSetTheme,
      resolvedScheme,
      isOrgBrandingEnabled,
      hasCustomBranding,
      hasOwnBranding,
      hasOwnLogo,
      isBrandingResolved,
      isBrandingResolvedForProject,
      isThemeSettled,
      syncCustomBranding,
      syncOwnLogo,
      appName,
      syncAppName,
      customFaviconUrl,
      bumpFaviconCacheBuster,
      syncCustomFavicon,
      logoCacheBusters,
      bumpLogoCacheBuster,
    }),
    [
      theme,
      handleSetTheme,
      resolvedScheme,
      isOrgBrandingEnabled,
      hasCustomBranding,
      hasOwnBranding,
      hasOwnLogo,
      isBrandingResolved,
      isBrandingResolvedForProject,
      isThemeSettled,
      syncCustomBranding,
      syncOwnLogo,
      appName,
      syncAppName,
      customFaviconUrl,
      bumpFaviconCacheBuster,
      syncCustomFavicon,
      logoCacheBusters,
      bumpLogoCacheBuster,
    ],
  );

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  /* istanbul ignore next */
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
